/**
 * Conquest: how a line on the world map becomes a battle, and how the battle
 * becomes a line on the world map again.
 *
 * This is the seam, and it is deliberately narrow. The campaign never simulates
 * a fight. It produces a `PendingBattle` describing who is attacking whom with
 * how much, and then one of exactly two things happens to it:
 *
 *   `battleConfigFor` turns it into a `BattleConfig` and the existing battle
 *   mode fights it for real, on the field, with the same movement, morale and
 *   fatigue code every other fight in this game uses. The result comes back
 *   through `Cmd.CampaignResolveBattle`.
 *
 *   `autoResolveBattle` settles it abstractly from the two strength numbers and
 *   the world's seeded RNG. This is what AI nations fight each other with — a
 *   campaign in which twenty AI battles a turn each required a tactical world
 *   would be unplayable — and it is what the player gets when they decline to
 *   fight one by hand.
 *
 * Both paths end in the same `applyBattleResult`, so there is exactly one
 * description of what winning a battle does to the map.
 */

import { clamp, clamp01 } from '../../core/math.ts';
import { nextFloat, type Rng } from '../../core/rng.ts';
import { unitType } from '../../data/units.ts';
import { nationCapital } from '../../data/nations.ts';
import { provinceDef } from '../../data/provinces.ts';
import { Team } from '../types.ts';
import { resolveArmyHeroesAbstractly } from './heroes.ts';
import type { World } from '../world.ts';
import type { BattleConfig } from '../battle.ts';
import { neighbours } from './graph.ts';
import { onProvinceCaptured } from './holdings.ts';
import { isVillageHolding } from './villages.ts';
import { breakShelter, shelteredIn, shelterSupport, SHELTER_BREAKOUT_SURVIVE } from './garrisons.ts';
import {
  applyLosses,
  armyPoints,
  armyModels,
  findArmy,
  nationFactionId,
  removeArmy,
} from './armies.ts';
import {
  checkElimination,
  defenceMultiplier,
  garrisonCap,
  logLine,
  nationName,
  provinceName,
  terrainDefence,
} from './state.ts';
import { campAt, campLoot, removeCamp } from './bandits.ts';
import { recordBattleScore, recordCaptureScore } from './war.ts';
import { roadName, settleRoadBattle, standDown, type RoadContact } from './marches.ts';
import {
  ArmyStance,
  BANDIT_NATION,
  BattleKind,
  CAMP_LOOT_GOLD,
  CAMP_LOOT_IRON,
  CONQUEST_UNREST,
  WRECK_MAX,
  WRECK_ON_STORM,
  type Army,
  type BanditCamp,
  type CampaignState,
  type PendingBattle,
} from './types.ts';

/**
 * Stand a battle up. Returns the pending record, which the turn machine parks
 * on the campaign until somebody resolves it.
 */
export function openBattle(
  c: CampaignState,
  seed: number,
  province: number,
  attacker: Army,
  defenderArmy: Army | undefined,
): PendingBattle {
  const prov = c.provinces[province];
  const defenderNation = prov.owner;
  const mult = defenceMultiplier(c, province);

  const attackerPoints = Math.max(1, Math.round(armyPoints(attacker)));
  const garrison = defenderNation >= 0 ? prov.garrison : prov.garrison;
  /**
   * Everything else that is inside the walls.
   *
   * `PendingBattle` carries exactly one defending army, and widening it to a
   * list would touch the save, the hash, both resolution paths and battle mode's
   * muster — for a case that is rare and that has an honest cheaper answer. The
   * second and third host behind the gate fight as *support*: their points are
   * on the defence, and `applyBattleResult` takes their losses at the same
   * fraction the named defender takes. What they do not get is to be the army
   * the player commands on the field, which is the right compromise: you took
   * two hosts inside one gate, and one of them is the reserve.
   */
  const support = defenderArmy
    ? shelterSupport(c, province).reduce((sum, a) => sum + armyPoints(a), 0)
    : 0;
  const defenderPoints = Math.max(
    1,
    Math.round(((defenderArmy ? armyPoints(defenderArmy) : 0) + support + garrison) * mult),
  );

  /**
   * Human, not *the* human.
   *
   * These decided the scenario and whether the fight is offered rather than
   * rolled, and they asked `c.playerNation` — which in a room is a different
   * answer on every client, inside a simulation whose whole contract is that
   * it is not. A battle between two crowns would have been fought on one
   * machine and auto-resolved on another. The question the shared state can
   * answer is whether a *person* is in it; which person, and which side they
   * are on, is the client's to work out.
   */
  const playerAttacks = c.humanNations.includes(attacker.owner);
  const playerDefends = c.humanNations.includes(defenderNation);
  const fortified = prov.fort > 0;

  // Scenario choice is a readable mapping from the strategic picture, not a
  // roll: walls mean an assault, open ground means a field battle.
  let scenario = 'open_field';
  if (fortified) scenario = playerAttacks ? 'assault' : 'hold_the_line';

  const pending: PendingBattle = {
    province,
    kind: BattleKind.Province,
    roadTo: -1,
    interceptor: -1,
    attackerNation: attacker.owner,
    defenderNation,
    attackerArmy: attacker.id,
    defenderArmy: defenderArmy ? defenderArmy.id : -1,
    attackerPoints,
    defenderPoints,
    scenario,
    playerFights: playerAttacks || playerDefends,
    playerAttacks,
    seed,
  };
  c.pending = pending;

  logLine(
    c,
    `${nationName(c, attacker.owner)} falls on ${provinceName(province)} — ` +
      `${attackerPoints} against ${defenderPoints}.`,
  );
  return pending;
}

/**
 * A battle on a road, between two hosts neither of which is standing anywhere.
 *
 * No terrain multiplier and no walls: a road battle is fought on ground neither
 * side chose, which is the entire difference between catching an army and
 * meeting one where it wanted to be met. Nothing changes hands — see
 * `applyBattleResult` — so this is the campaign's one fight that is purely about
 * the armies in it.
 */
export function openRoadBattle(
  c: CampaignState,
  seed: number,
  contact: RoadContact,
): PendingBattle {
  const { attacker, defender } = contact;
  const playerAttacks = c.humanNations.includes(attacker.owner);
  const pending: PendingBattle = {
    province: contact.from,
    kind: BattleKind.Road,
    roadTo: contact.to,
    interceptor: contact.interceptor,
    attackerNation: attacker.owner,
    defenderNation: defender.owner,
    attackerArmy: attacker.id,
    defenderArmy: defender.id,
    attackerPoints: Math.max(1, Math.round(armyPoints(attacker))),
    defenderPoints: Math.max(1, Math.round(armyPoints(defender))),
    scenario: 'open_field',
    playerFights: playerAttacks || c.humanNations.includes(defender.owner),
    playerAttacks,
    seed,
  };
  c.pending = pending;
  return pending;
}

/**
 * A battle against brigands squatting on a province.
 *
 * The camp fights at the terrain's defence multiplier but not the walls': it is
 * camped outside them, and if it were inside them it would be a rebellion rather
 * than a camp. An attacker clearing a camp on its own ground brings half the
 * garrison out with it, which is what stops a nation needing a full field host
 * to answer a problem in its own back country.
 */
export function openCampBattle(
  c: CampaignState,
  seed: number,
  province: number,
  attacker: Army,
  camp: BanditCamp,
): PendingBattle {
  const ownGround = c.provinces[province].owner === attacker.owner;
  const support = ownGround ? c.provinces[province].garrison * 0.5 : 0;
  const playerAttacks = c.humanNations.includes(attacker.owner);
  const pending: PendingBattle = {
    province,
    kind: BattleKind.Camp,
    roadTo: -1,
    interceptor: -1,
    attackerNation: attacker.owner,
    defenderNation: BANDIT_NATION,
    attackerArmy: attacker.id,
    defenderArmy: -1,
    attackerPoints: Math.max(1, Math.round(armyPoints(attacker) + support)),
    defenderPoints: Math.max(1, Math.round(camp.strength * terrainDefence(province))),
    scenario: 'open_field',
    playerFights: playerAttacks,
    playerAttacks,
    seed,
  };
  c.pending = pending;
  logLine(
    c,
    `${attacker.name} goes into the hills after the camp at ${provinceName(province)} — ` +
      `${pending.attackerPoints} against ${pending.defenderPoints}.`,
  );
  return pending;
}

/**
 * True when the pending battle is the one that must be fought in the player's
 * own city rather than on an abstract field.
 *
 * It is a predicate here rather than a condition at the call site because the
 * condition grew a third clause the moment there was more than one kind of
 * battle. A road battle carries the *attacker's* end of the lane in `province`,
 * and a player host marching out of its own capital, caught on the road, is a
 * battle whose `province` is the capital and whose player is the defender —
 * which the old two-clause test read as "they are at your gates" and would have
 * answered by standing up a siege of a city nobody was anywhere near.
 */
export function isHomeAssault(c: CampaignState): boolean {
  const p = c.pending;
  if (!p) return false;
  // Visiting a settlement is no longer required to defend its actual streets.
  return (
    p.kind === BattleKind.Province &&
    p.defenderNation === c.playerNation &&
    isTownAssault(c)
  );
}

/** Roads and camps are field battles, even when their province names a city. */
export function isTownAssault(c: CampaignState): boolean {
  const p = c.pending;
  const prov = p?.kind === BattleKind.Province ? c.provinces[p.province] : null;
  return !!prov && (isVillageHolding(c, p!.province) || prov.holdingKind === 'city' || prov.fort > 0 || prov.settled > 0 || prov.tier >= 4);
}

/**
 * The pending battle, as something `createBattle` can build a field from.
 *
 * Returns null when there is nothing pending or when the player is not in it —
 * an AI-versus-AI battle is never worth building a tactical world for.
 */
/**
 * Which colours this client commands on the pending field.
 *
 * `Team.Player` on an ordinary field, and on a shared one whichever side this
 * client's crown is actually on — the attacker's colours are canonical, so a
 * defender commands the enemy's.
 */
export function myBattleTeam(w: World): number {
  const c = w.campaign;
  const p = c?.pending;
  if (!c || !p) return Team.Player;
  const pvp =
    c.humanNations.includes(p.attackerNation) && c.humanNations.includes(p.defenderNation);
  if (!pvp) return Team.Player;
  return p.attackerNation === c.playerNation ? Team.Player : Team.Invader;
}

export function battleConfigFor(w: World): BattleConfig | null {
  const c = w.campaign;
  if (!c || !c.pending || !c.pending.playerFights) return null;
  const p = c.pending;

  /**
   * Which side *this* client is on, which `p.playerAttacks` can no longer
   * say: it now means "a human attacks", and in a room between two crowns
   * both sides are human. The board's fields name the two nations; the
   * client names itself.
   */
  const iAttack = p.attackerNation === c.playerNation;
  if (!iAttack && p.defenderNation !== c.playerNation) return null;

  const playerNationIdx = c.playerNation;
  const enemyNationIdx = iAttack ? p.defenderNation : p.attackerNation;
  const playerFactionId = nationFactionId(c, playerNationIdx);
  /**
   * Brigands muster from the Gutter Congregation's roster.
   *
   * They are not a nation and have no roster of their own, and giving them one
   * would mean sixty new unit definitions for an opponent that exists to be
   * cleared in the first ten turns. The Congregation is the game's rabble — thin
   * armour, numbers, improvised weapons — which is exactly what a bandit camp
   * should feel like across the field.
   */
  const enemyFactionId =
    enemyNationIdx === BANDIT_NATION
      ? 'gutter'
      : enemyNationIdx >= 0
        ? nationFactionId(c, enemyNationIdx)
        : 'ironfang';

  const playerPoints = iAttack ? p.attackerPoints : p.defenderPoints;
  const enemyPoints = iAttack ? p.defenderPoints : p.attackerPoints;

  /**
   * The town the defender actually holds, handed to the field.
   *
   * Three numbers the map already keeps: the rung the holding stands on, how
   * hard it has been fortified, and whether it is somebody's seat. `createBattle`
   * turns them into a wall circuit, four gates, streets and however many roofs
   * that rung has earned — see `raiseTown`. Only for a battle *at a province*:
   * a road battle is fought on ground neither side chose and a bandit camp is
   * camped outside the walls by definition, so neither has a town in it.
   */
  const prov = p.kind === BattleKind.Province ? c.provinces[p.province] : undefined;
  const town =
    prov && isTownAssault(c)
      ? { tier: Math.max(1, prov.tier), fort: prov.fort, capital: p.province === nationCapital(c.nations[p.defenderNation]?.def ?? 0) }
      : undefined;

  /**
   * The country the fight is in.
   *
   * `WorldConfig.biome` has carried a province's terrain into the *settlement*
   * since there were countries, and never into a battle — so a field in the
   * Rimewreath and a field in the sand sea were the same temperate valley, the
   * exact complaint the `biome` note in `world.ts` is written against. It costs
   * one line and `src/sim/field.ts` reads it: mountains get real relief, the
   * desert gets dunes and no wood, the fens get water and bog and almost no
   * height. A road battle has a province too — it is fought *in* one, on the
   * lane between two — so this is taken for every kind of battle rather than
   * only for a province assault.
   */
  const biome = p.province >= 0 ? provinceDef(p.province).terrain : undefined;

  /**
   * A field between two crowns is built the same way on both machines.
   *
   * `pvp` says so, and the sides are named by the *board* rather than by
   * whoever is asking: the attacker's colours are always `Team.Player`. Each
   * client then knows which of the two is its own — see `myBattleTeam` — and
   * neither world is a mirror of the other, which is what lets both step the
   * one field in step.
   */
  const pvp =
    c.humanNations.includes(p.attackerNation) && c.humanNations.includes(p.defenderNation);
  const attackerFactionId = nationFactionId(c, p.attackerNation);
  const defenderFactionId =
    p.defenderNation === BANDIT_NATION
      ? 'gutter'
      : p.defenderNation >= 0
        ? nationFactionId(c, p.defenderNation)
        : 'ironfang';

  if (pvp) {
    return {
      seed: p.seed,
      province: p.province,
      factionId: attackerFactionId,
      difficultyId: w.config.difficultyId,
      sandbox: false,
      scenario: town ? 'assault' : p.scenario as BattleConfig['scenario'],
      enemyFactionId: defenderFactionId,
      pvp: true,
      points: Math.round((p.attackerPoints + p.defenderPoints) / 2),
      playerPoints: p.attackerPoints,
      enemyPoints: p.defenderPoints,
      biome,
      /**
       * A shared field gets the town too, and did not.
       *
       * This branch was written before a fortified province stood a town up and
       * simply never grew the field — so two crowns fighting over a walled
       * Great City fought in an empty meadow, on both machines, agreeing
       * perfectly about the wrong place. `town` is read off the board, which is
       * canonical, so both clients still build the identical world.
       */
      town,
    };
  }

  return {
    seed: p.seed,
    province: p.province,
    factionId: playerFactionId,
    difficultyId: w.config.difficultyId,
    sandbox: false,
    scenario: town ? (iAttack ? 'assault' : 'hold_the_line') : p.scenario as BattleConfig['scenario'],
    enemyFactionId,
    // `points` is battle mode's own budget dial; the two explicit budgets below
    // override it so the field reflects what the map actually says is there.
    points: Math.round((playerPoints + enemyPoints) / 2),
    playerPoints,
    enemyPoints,
    biome,
    town,
  };
}

/**
 * Take a fought battle's result back.
 *
 * `playerWon` and the two survival fractions are exactly what battle mode can
 * report: who broke, and how many models each side still had standing.
 */
export function resolveFoughtBattle(
  w: World,
  reporter: number,
  playerWon: boolean,
  playerSurvive: number,
  enemySurvive: number,
  foughtInTown = false,
): void {
  const c = w.campaign;
  if (!c || !c.pending) return;
  const p = c.pending;
  // The result arrives from whichever client fought it, phrased as "the player
  // won" — so it is turned back into attacker/defender using *that seat's*
  // side, not the shared "a human attacks" and not the crown whose screen this
  // happens to be. On a server there is no such screen, and reading one would
  // have turned a defender's victory into their defeat.
  const iAttacked = p.attackerNation === reporter;
  const attackerWon = iAttacked ? playerWon : !playerWon;
  const attackerSurvive = iAttacked ? playerSurvive : enemySurvive;
  const defenderSurvive = iAttacked ? enemySurvive : playerSurvive;
  applyBattleResult(w, attackerWon, attackerSurvive, defenderSurvive);
  // Physical damage has already been recorded in the persistent settlement.
  // Charging its abstract sack on entry would destroy the same town twice.
  if (foughtInTown && p.kind === BattleKind.Province) {
    c.provinces[p.province].wreck = 0;
    c.provinces[p.province].settled = Math.max(1, c.provinces[p.province].settled || c.turn);
  }
}

/**
 * Settle the pending battle from its two numbers and the seeded RNG.
 *
 * The odds curve is deliberately steeper than linear — a three-to-two advantage
 * should be a likely win rather than a coin flip with extra steps — but it is
 * never a certainty, because a strategic layer in which the bigger stack always
 * wins is arithmetic rather than a game.
 */
export function autoResolveBattle(w: World): void {
  const c = w.campaign;
  if (!c || !c.pending) return;
  const p = c.pending;
  const r = rollBattle(w.rngCampaign, p.attackerPoints, p.defenderPoints);
  resolveArmyHeroesAbstractly(c, r.attackerWon);
  applyBattleResult(w, r.attackerWon, r.attackerSurvive, r.defenderSurvive);
}

/** What an abstract fight comes to: who broke, and how much of each side is left. */
export interface BattleRoll {
  attackerWon: boolean;
  attackerSurvive: number;
  defenderSurvive: number;
}

/**
 * The odds curve and the butcher's bill, in one place.
 *
 * Split out of `autoResolveBattle` when fleets arrived, because a battle at sea
 * is settled by exactly this arithmetic and never by hand — there is no naval
 * battle mode and there should not be one. Two copies of "how a fight goes when
 * nobody plays it" would have drifted, and the drift would have shown up as the
 * sea being systematically kinder or crueller than the land for no reason
 * anybody could name.
 *
 * Two draws from the RNG, always, whichever way the fight goes. That is not
 * tidiness: the stream is shared with everything else in the campaign, so a
 * function that drew a different number of times depending on the result would
 * make every later roll in the month depend on this one, and determinism would
 * hold while reproducibility across a small change stopped meaning anything.
 */
export function rollBattle(rng: Rng, attackerPoints: number, defenderPoints: number): BattleRoll {
  const atk = Math.max(1, attackerPoints);
  const def = Math.max(1, defenderPoints);
  // x^1.5 via sqrt: exact under IEEE, unlike Math.pow with a fractional exponent.
  const a15 = atk * Math.sqrt(atk);
  const d15 = def * Math.sqrt(def);
  const odds = a15 / (a15 + d15);

  const attackerWon = nextFloat(rng) < odds;
  const edge = attackerWon ? atk / def : def / atk;
  // A one-sided win costs the winner little and the loser almost everything.
  const margin = clamp01((edge - 1) / 1.5);
  const winnerSurvive = clamp(0.42 + 0.44 * margin + 0.12 * nextFloat(rng), 0.3, 0.96);
  const loserSurvive = clamp(0.3 - 0.24 * margin + 0.1 * nextFloat(rng), 0, 0.42);

  return {
    attackerWon,
    attackerSurvive: attackerWon ? winnerSurvive : loserSurvive,
    defenderSurvive: attackerWon ? loserSurvive : winnerSurvive,
  };
}

/**
 * What winning does to the map. The single description, shared by both paths.
 */
export function applyBattleResult(
  w: World,
  attackerWon: boolean,
  attackerSurvive: number,
  defenderSurvive: number,
): void {
  const c = w.campaign;
  if (!c || !c.pending) return;
  const p = c.pending;
  c.pending = null;

  const prov = c.provinces[p.province];
  const attacker = findArmy(c, p.attackerArmy);
  const defender = p.defenderArmy >= 0 ? findArmy(c, p.defenderArmy) : undefined;

  if (p.kind === BattleKind.Road) {
    applyRoadResult(c, p, attackerWon, attackerSurvive, defenderSurvive, attacker, defender);
    return;
  }
  if (p.kind === BattleKind.Camp) {
    applyCampResult(c, p, attackerWon, attackerSurvive, defenderSurvive, attacker);
    return;
  }

  let attackerLost = 0;
  let defenderLost = 0;
  if (attacker) attackerLost = applyLosses(attacker, attackerSurvive);
  if (defender) defenderLost = applyLosses(defender, defenderSurvive);
  const support = shelteredIn(c, p.province).filter((a) => a.owner === p.defenderNation && a !== defender);
  for (const a of support) {
    defenderLost += applyLosses(a, defenderSurvive);
    if (a.regiments.length === 0) removeArmy(c, a.id);
  }
  // The garrison takes the same beating the defending army does, and is broken
  // outright when the walls are carried.
  const garrisonBefore = prov.garrison;
  prov.garrison = attackerWon ? 0 : Math.round(prov.garrison * clamp01(defenderSurvive));
  defenderLost += Math.round((garrisonBefore - prov.garrison) * 0.25);

  const attackerDead = !attacker || attacker.regiments.length === 0;
  const defenderDead = defender !== undefined && defender.regiments.length === 0;
  if (defenderDead && defender) removeArmy(c, defender.id);
  if (attackerDead && attacker) removeArmy(c, attacker.id);

  /**
   * The ledger.
   *
   * Recorded here, in `applyBattleResult`, rather than on either resolution path,
   * because this is the one place both of them meet: a fight settled by hand on
   * the field and a fight settled by arithmetic between two AI crowns move the
   * war score identically, and there is no way for the two to drift apart.
   * `recordBattleScore` finds the war itself and does nothing if there is not one
   * — a fight over unclaimed ground is not a war and does not score.
   */
  if (attackerWon) {
    recordBattleScore(c, p.attackerNation, p.defenderNation, p.defenderPoints);
  } else {
    recordBattleScore(c, p.defenderNation, p.attackerNation, p.attackerPoints);
  }

  if (attackerWon) {
    if (attacker && !attackerDead) {
      attacker.victories++;
      attacker.stance = ArmyStance.Field;
      attacker.siegeTurns = 0;
    }
    if (defender && !defenderDead) retreat(c, defender, p.province);
    for (const a of support) if (a.regiments.length > 0) retreat(c, a, p.province);
    captureProvince(c, p.province, p.attackerNation);
    logLine(
      c,
      `${provinceName(p.province)} falls to ${nationName(c, p.attackerNation)}. ` +
        `${attackerLost} of theirs, ${defenderLost} of the defenders.`,
    );
  } else {
    if (defender && !defenderDead) defender.victories++;
    if (attacker && !attackerDead) retreat(c, attacker, p.province);
    prov.siegeBy = -1;
    prov.siegeT = 0;
    logLine(
      c,
      `${provinceName(p.province)} holds. ${nationName(c, p.attackerNation)} leaves ` +
        `${attackerLost} on the ground.`,
    );
  }

  checkElimination(c);
}

/**
 * A road battle settles nothing except which host is still walking.
 *
 * No ground changes hands, because none was at stake: the fight happened between
 * two provinces. The loser is thrown back to the end it set out from, the winner
 * pays a turn for having stopped to fight, and an interceptor that wins returns
 * to the province it went out from. That last point is what keeps interception
 * from being a free extra move: catching an army costs you your turn too.
 */
function applyRoadResult(
  c: CampaignState,
  p: PendingBattle,
  attackerWon: boolean,
  attackerSurvive: number,
  defenderSurvive: number,
  attacker: Army | undefined,
  defender: Army | undefined,
): void {
  let attackerLost = 0;
  let defenderLost = 0;
  if (attacker) attackerLost = applyLosses(attacker, attackerSurvive);
  if (defender) defenderLost = applyLosses(defender, defenderSurvive);

  const attackerDead = !attacker || attacker.regiments.length === 0;
  const defenderDead = !defender || defender.regiments.length === 0;

  const winner = attackerWon ? attacker : defender;
  const loser = attackerWon ? defender : attacker;
  if (winner && !(attackerWon ? attackerDead : defenderDead)) winner.victories++;

  // `attackerArmy` is the interceptor whenever the contact was an ordered chase;
  // a head-on meeting has no interceptor and passes -1, which matches nobody.
  settleRoadBattle(c, winner, loser, p.interceptor);

  if (defenderDead && defender) removeArmy(c, defender.id);
  if (attackerDead && attacker) removeArmy(c, attacker.id);

  logLine(
    c,
    `${nationName(c, attackerWon ? p.attackerNation : p.defenderNation)} has the better of it on ` +
      `${roadName(c, p.province, p.roadTo)} — ${attackerLost} and ${defenderLost} left on the road.`,
  );
  checkElimination(c);
}

/**
 * Clearing a camp, or failing to.
 *
 * A win removes the camp and pays for it: brigands keep what they take, and
 * whoever takes it back gets it. A loss leaves the camp standing but hurt, so a
 * second attempt is cheaper than the first — a player who nearly cleared a camp
 * has made progress, not a mistake.
 *
 * The province never changes hands either way. Bandits do not hold ground in the
 * sense the rest of this layer means it, and a nation that walks into unclaimed
 * ground after clearing it takes it through the ordinary contact rules next turn.
 */
function applyCampResult(
  c: CampaignState,
  p: PendingBattle,
  attackerWon: boolean,
  attackerSurvive: number,
  defenderSurvive: number,
  attacker: Army | undefined,
): void {
  const camp = campAt(c, p.province);
  let attackerLost = 0;
  if (attacker) attackerLost = applyLosses(attacker, attackerSurvive);
  const attackerDead = !attacker || attacker.regiments.length === 0;

  if (attackerWon) {
    if (camp) {
      const loot = campLoot(camp, CAMP_LOOT_GOLD, CAMP_LOOT_IRON);
      const nation = c.nations[p.attackerNation];
      if (nation) {
        nation.treasury.gold += loot.gold;
        nation.treasury.iron += loot.iron;
      }
      removeCamp(c, p.province);
      logLine(
        c,
        `The camp at ${provinceName(p.province)} is burned out. ` +
          `${loot.gold} gold comes back down the hill with ${nationName(c, p.attackerNation)}, ` +
          `and ${attackerLost} do not.`,
      );
    }
    if (attacker && !attackerDead) {
      attacker.victories++;
      attacker.stance = ArmyStance.Field;
      attacker.siegeTurns = 0;
    }
  } else {
    // The camp took a beating even in winning; a floor stops a near-miss from
    // making the next attempt trivial.
    if (camp) camp.strength = Math.max(30, Math.round(camp.strength * clamp01(defenderSurvive)));
    logLine(
      c,
      `${provinceName(p.province)} keeps its brigands. ${attackerLost} did not come back.`,
    );
    if (attacker && !attackerDead) retreat(c, attacker, p.province);
  }

  if (attackerDead && attacker) removeArmy(c, attacker.id);
  checkElimination(c);
}

/** Ownership changes. Everything a conquest does to a province is here. */
export function captureProvince(c: CampaignState, province: number, nation: number): void {
  const prov = c.provinces[province];
  const from = prov.owner;
  /**
   * The gates open with hosts still inside them.
   *
   * They are neither captured wholesale nor let out free. Either extreme is a
   * bad rule: taking a player's army away without a fight is the kind of thing
   * people stop playing over, and releasing it intact would make hiding the
   * correct answer to every siege in the game. So they cut their way out and
   * half of them do not — the same `applyLosses` a lost battle uses, on the
   * same ledger — and whatever is left is standing in the open on ground that
   * has just become somebody else's, which the ordinary machinery then handles
   * exactly as it handles any host caught abroad.
   *
   * Before the flag moves, because `breakShelter` reads who is inside and the
   * captor's own hosts, if any were sheltered here, are not the ones breaking
   * out.
   */
  for (const a of breakShelter(c, province)) {
    if (a.owner === nation) continue;
    const lost = applyLosses(a, SHELTER_BREAKOUT_SURVIVE);
    if (lost > 0) {
      logLine(c, `${a.name} cuts its way out of ${provinceName(province)}, leaving ${lost}.`);
    }
    if (a.regiments.length === 0) removeArmy(c, a.id);
  }
  /**
   * Two things that are not about the walls.
   *
   * The crown that just lost this ground is the crown that will want it back —
   * `claimedBy` is the whole of the claim system and it is set here because here
   * is where losing happens. And if the two were at war, the war score moves:
   * ground is the single largest thing that can happen to it, which is what makes
   * a siege worth running to the end rather than abandoning for a better target.
   */
  if (from >= 0 && from !== nation) {
    prov.claimedBy = from;
    recordCaptureScore(c, nation, from, province);
  }
  /**
   * What the sack does to the holding, before the new owner is written.
   *
   * A tier is knocked off, half-built works are abandoned and any warehouse here
   * is emptied into the captor's pocket — the three things that stop the ladder
   * from being a pure accumulation. Ground that changes hands every other decade
   * never gets tall; the tall province is the one that has been *held*. It runs
   * before `prov.owner` moves because the sack of a yard needs to know who is
   * taking it *from* whom.
   */
  onProvinceCaptured(c, province, nation);

  /**
   * And what it does to the town, which is a different thing from what it does
   * to the holding.
   *
   * `onProvinceCaptured` above knocks a rung off the ladder — the abstract
   * measure the strategic map scores this ground by. This is the other half of
   * the same sentence, and it is owed to whoever next walks in: a fraction of
   * the roofs, the people and the walls of the actual settlement. Recorded here
   * rather than applied here because there may be no settlement world for this
   * province at all — nobody has ever stood in it — and a debt is the only
   * shape that is correct both ways. `Cmd.EnterHold` clears it, so one sack is
   * paid for once.
   *
   * Written on every capture, including one that takes a town *off* the player:
   * the world stays in the save, unreachable while somebody else holds the
   * ground, and the bill is waiting if they take it back.
   */
  prov.wreck = Math.min(WRECK_MAX, Math.max(prov.wreck, WRECK_ON_STORM));

  prov.owner = nation;
  prov.garrison = Math.round(garrisonCap(c, province) * 0.25);
  prov.unrest = CONQUEST_UNREST;
  prov.siegeBy = -1;
  prov.siegeT = 0;
  // A stormed wall is a broken wall.
  if (prov.fort > 0) prov.fort--;
}

/**
 * A beaten army falls back. It prefers its own ground; failing that, anywhere
 * its enemy is not; failing that, it is finished where it stands.
 */
function retreat(c: CampaignState, a: Army, from: number): void {
  const options = neighbours(c, from);
  let target = -1;
  for (const n of options) {
    if (c.provinces[n].owner === a.owner) {
      target = n;
      break;
    }
  }
  if (target < 0) {
    for (const n of options) {
      if (c.provinces[n].owner !== c.provinces[from].owner) {
        target = n;
        break;
      }
    }
  }
  if (target < 0) {
    logLine(c, `${a.name} is run down at ${provinceName(from)}. There was nowhere to go.`);
    removeArmy(c, a.id);
    return;
  }
  // `standDown` rather than `landArmy`: a host driven off a province is not
  // still walking to wherever it was going before somebody beat it. Keeping the
  // order was how a march that was interrupted came back as a destination
  // nothing would ever act on again — see `standDown`.
  standDown(a, target);
  a.sheltered = false;
  a.stance = ArmyStance.Field;
  a.siegeTurns = 0;
  a.supply = Math.min(a.supply, 0.6);
}

/** Models still standing, for the world-map panel and the result screen. */
export function pendingStrength(c: CampaignState): { attacker: number; defender: number } {
  if (!c.pending) return { attacker: 0, defender: 0 };
  const atk = findArmy(c, c.pending.attackerArmy);
  const def = c.pending.defenderArmy >= 0 ? findArmy(c, c.pending.defenderArmy) : undefined;
  return { attacker: atk ? armyModels(atk) : 0, defender: def ? armyModels(def) : 0 };
}

/** Rough model count a points budget buys, for the "what am I facing" readout. */
export function modelsForPoints(typeIdx: number, points: number): number {
  const t = unitType(typeIdx);
  return t.threat > 0 ? Math.round(points / t.threat) : 0;
}
